import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import AICoachFloating from "@/components/lingo/AICoachFloating";
import api from "@/lib/api";
import { useExamMode } from "@/components/lingo/DashboardLayout";
import {
  Headphones, BookOpen, PencilLine, Mic, Clock, ChevronRight, GraduationCap, Award, Play
} from "lucide-react";

const SECTION_ICON = { Listening: Headphones, Reading: BookOpen, Writing: PencilLine, Speaking: Mic };

export default function MockExams() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [examMode] = useExamMode();

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [examMode]);

  const load = async () => {
    const examType = examMode === "GENERAL" ? "IELTS_GENERAL" : "IELTS_ACADEMIC";
    const [r, h] = await Promise.all([
      api.get("/mock-exams/", { params: { exam_type: examType } }),
      api.get("/mock-exams/sessions/mine").catch(() => ({ data: [] })),
    ]);
    setItems(r.data);
    setHistory(h.data || []);
  };

  return (
    <DashboardLayout hideHeader>
      <div className="flex items-center justify-between pb-3 border-b border-[#F77F00]/30 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center"><GraduationCap className="w-4 h-4" /></div>
          <h1 data-testid="page-title" className="text-[#F77F00] font-display font-semibold text-lg">Full Mock Tests</h1>
        </div>
        <div className="text-sm text-slate-500">{examMode === "GENERAL" ? "IELTS General" : "IELTS Academic"} · {items.length} available</div>
      </div>

      <p className="text-sm text-slate-600 mb-6 max-w-3xl">
        Realistic full-length combined exams. Each mock walks you sequentially through Listening → Reading → Writing → Speaking
        as in the real test day. Your section scores aggregate into a single predicted band.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((m, idx) => {
          const lastSession = history.find((h) => h.exam_id === m.id);
          return (
            <div key={m.id} data-testid={`mock-${m.id}`} className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#F77F00]/50 hover:shadow-[0_20px_40px_-20px_rgba(11,45,92,0.18)] transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#F77F00]">Mock #{idx + 1}</div>
                <div className="flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" /> {m.duration_minutes} min</div>
              </div>
              <h3 className="font-display text-lg text-[#003049] leading-snug">{m.title}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{m.description}</p>

              <div className="mt-4 flex gap-2">
                {m.sections.map((s) => {
                  const Icon = SECTION_ICON[s.label] || Headphones;
                  return (
                    <div key={s.order} className="flex-1 flex flex-col items-center py-2 rounded-lg bg-slate-50 border border-slate-100">
                      <Icon className="w-4 h-4 text-[#003049]" />
                      <span className="text-[10px] text-slate-500 mt-1">{s.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Level · <span className="font-medium text-slate-700">{m.level || "All"}</span>
                </div>
                {lastSession?.overall_band ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                    <Award className="w-3 h-3" /> Last: {lastSession.overall_band}
                  </span>
                ) : null}
              </div>

              <Link to={`/app/mock/${m.id}`} data-testid={`mock-start-${m.id}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#F77F00] hover:bg-[#D26900] text-white font-medium py-2.5 rounded-xl">
                <Play className="w-4 h-4 fill-white" /> Start Mock
              </Link>
            </div>
          );
        })}
        {!items.length && <div className="col-span-3 text-center py-16 text-slate-400">No mock exams available for this mode.</div>}
      </div>

      {history.length > 0 && (
        <section className="mt-10">
          <h2 className="text-[#F77F00] font-semibold text-base mb-4">Recent attempts</h2>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-b-0">
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">{items.find((m) => m.id === h.exam_id)?.title || h.exam_id}</div>
                  <div className="text-xs text-slate-500">{h.started_at?.slice(0, 16).replace("T", " ")} · {h.status}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl text-[#003049]">{h.overall_band ?? "—"}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-widest">overall band</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <AICoachFloating />
    </DashboardLayout>
  );
}
